/**
 * ui_window.ts — SWG-style desktop windows for the in-game UI.
 *
 * Star Wars Galaxies let every UI element be moved and resized (its ALT "Data
 * mode" toggled the affordances) and remembered the layout per character, with
 * window appearance/opacity in Options. The Atlas adaptation, tuned for a web
 * client whose panels already own a free cursor:
 *
 *   drag       — grab the title bar (pointer capture; clamped to the viewport)
 *   resize     — the bottom-right grip, per-window minimums
 *   shade      — double-click the title (or the — button): rolls up to the bar
 *   opacity    — the droplet button cycles 100 → 85 → 70 → 55 %
 *   focus      — pointerdown anywhere raises the window (shared z counter)
 *   persist    — {x, y, w, h, opacity, shaded} per window id in localStorage,
 *                clamped back into view on restore (monitors change)
 *   close      — the × button (the owning module's toggle re-opens it)
 *
 * makeWindow() is the chrome; the owning module renders INTO `body` and drives
 * visibility through setOpen()/isOpen() so its hotkey/menu-bar toggle and the
 * × button stay in perfect agreement.
 */

import { makeIcon } from './icons.js';

export interface WindowOptions {
  id: string;          // persistence key — stable across sessions
  title: string;
  icon?: string;       // icons.ts name for the title bar
  x?: number;          // defaults when nothing is persisted
  y?: number;
  width?: number;
  height?: number;     // omit → auto height (content-sized), still resizable
  minWidth?: number;
  minHeight?: number;
  onClose?: () => void;
}

export interface UiWindow {
  root: HTMLDivElement;
  body: HTMLDivElement;
  setOpen(open: boolean): void;
  isOpen(): boolean;
  setTitle(title: string): void;
}

interface Persisted { x: number; y: number; w: number; h: number; op: number; sh: boolean }

const OPACITY_STEPS = [1, 0.85, 0.7, 0.55];
let zCounter = 40;

function loadGeom(id: string): Persisted | null {
  try {
    const raw = localStorage.getItem(`atlas:win:${id}`);
    return raw ? (JSON.parse(raw) as Persisted) : null;
  } catch { return null; }
}

function saveGeom(id: string, p: Persisted): void {
  try { localStorage.setItem(`atlas:win:${id}`, JSON.stringify(p)); } catch { /* private mode */ }
}

function chromeButton(iconName: string, tip: string): HTMLButtonElement {
  const b = document.createElement('button');
  b.title = tip;
  // Colours and the hover come from the theme sheet (lib/ui_theme.ts, [data-ax-chrome]) — geometry only here.
  b.dataset.axChrome = '1';
  b.style.cssText =
    'width:22px;height:22px;display:flex;align-items:center;justify-content:center;' +
    'background:transparent;border:1px solid transparent;cursor:pointer;padding:0';
  b.appendChild(makeIcon(iconName, 13));
  return b;
}

export function makeWindow(opts: WindowOptions): UiWindow {
  const saved = loadGeom(opts.id);
  const minW = opts.minWidth ?? 240;
  const minH = opts.minHeight ?? 140;

  const root = document.createElement('div');
  // The gamepad's UI navigator finds panels by this attribute (2026-09-16) —
  // there is no window registry, only a shared z counter, so the topmost open
  // panel is found by querying the DOM and taking the highest z-index.
  root.dataset.atlasNav = opts.id;
  // THE TOKEN LAW: the window's MATERIAL (ground, border, seam, frame, blur, type) is the theme's —
  // `[data-ax-window]` in lib/ui_theme.ts. Only layout lives inline.
  root.dataset.axWindow = opts.id;
  root.style.cssText =
    'position:fixed;z-index:40;display:none;flex-direction:column;overflow:hidden;user-select:none';

  // ── Title bar ──────────────────────────────────────────────────────────────
  const bar = document.createElement('div');
  bar.dataset.axBar = '1';
  bar.style.cssText = 'display:flex;align-items:center;gap:8px;padding:7px 8px 7px 12px;cursor:grab;flex:0 0 auto';
  const iconWrap = document.createElement('span');
  iconWrap.dataset.axIcon = '1';
  iconWrap.style.cssText = 'display:flex';
  iconWrap.appendChild(makeIcon(opts.icon ?? 'menu', 15));
  bar.appendChild(iconWrap);
  const titleEl = document.createElement('span');
  titleEl.textContent = opts.title;
  titleEl.dataset.axTitle = '1';
  titleEl.style.cssText = 'flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap';
  bar.appendChild(titleEl);
  const btnOpacity = chromeButton('opacity', 'Window opacity');
  const btnShade = chromeButton('shade', 'Roll up (double-click title too)');
  const btnClose = chromeButton('close', 'Close');
  // The gamepad's B button closes a panel through THIS element. Marking it is the
  // contract; sniffing the label is not — the first attempt matched a button by
  // the character '×' and found "Ultra 1.3×" in the graphics presets, so B set
  // the render preset instead of closing the menu.
  btnClose.dataset.atlasClose = '1';
  bar.appendChild(btnOpacity);
  bar.appendChild(btnShade);
  bar.appendChild(btnClose);
  root.appendChild(bar);

  // ── Body + resize grip ─────────────────────────────────────────────────────
  const body = document.createElement('div');
  body.style.cssText = 'flex:1 1 auto;overflow:auto;min-height:0';
  root.appendChild(body);
  const grip = document.createElement('div');
  grip.dataset.axGrip = '1';
  grip.style.cssText =
    'position:absolute;right:0;bottom:0;width:20px;height:20px;cursor:nwse-resize;' +
    'display:flex;align-items:flex-end;justify-content:flex-end;padding:2px';
  grip.appendChild(makeIcon('grip', 13));
  root.appendChild(grip);

  // ── Geometry state ─────────────────────────────────────────────────────────
  const state: Persisted = saved ?? {
    x: opts.x ?? Math.max(12, (window.innerWidth - (opts.width ?? 420)) / 2),
    y: opts.y ?? 64,
    w: opts.width ?? 420,
    h: opts.height ?? 0, // 0 = auto
    op: 1,
    sh: false,
  };
  let opIndex = Math.max(0, OPACITY_STEPS.findIndex((v) => Math.abs(v - state.op) < 0.01));
  if (opIndex < 0) opIndex = 0;

  const clamp = (): void => {
    state.x = Math.min(Math.max(-state.w + 60, state.x), window.innerWidth - 60);
    state.y = Math.min(Math.max(0, state.y), window.innerHeight - 34);
  };
  const apply = (): void => {
    clamp();
    root.style.left = `${state.x}px`;
    root.style.top = `${state.y}px`;
    root.style.width = `${state.w}px`;
    root.style.height = state.sh ? 'auto' : state.h > 0 ? `${state.h}px` : 'auto';
    root.style.maxHeight = '92vh';
    root.style.opacity = String(OPACITY_STEPS[opIndex]);
    body.style.display = state.sh ? 'none' : 'block';
    grip.style.display = state.sh ? 'none' : 'flex';
  };
  const persist = (): void => saveGeom(opts.id, { ...state, op: OPACITY_STEPS[opIndex] });

  // ── Interactions ───────────────────────────────────────────────────────────
  const raise = (): void => { root.style.zIndex = String(++zCounter); };
  root.addEventListener('pointerdown', raise);

  let drag: { px: number; py: number; x: number; y: number } | null = null;
  bar.addEventListener('pointerdown', (e) => {
    if ((e.target as HTMLElement).closest('button')) return;
    drag = { px: e.clientX, py: e.clientY, x: state.x, y: state.y };
    bar.setPointerCapture(e.pointerId);
    bar.style.cursor = 'grabbing';
  });
  bar.addEventListener('pointermove', (e) => {
    if (!drag) return;
    state.x = drag.x + (e.clientX - drag.px);
    state.y = drag.y + (e.clientY - drag.py);
    apply();
  });
  bar.addEventListener('pointerup', () => {
    if (!drag) return;
    drag = null;
    bar.style.cursor = 'grab';
    persist();
  });
  bar.addEventListener('dblclick', (e) => {
    if ((e.target as HTMLElement).closest('button')) return;
    state.sh = !state.sh;
    apply();
    persist();
  });

  let rez: { px: number; py: number; w: number; h: number } | null = null;
  grip.addEventListener('pointerdown', (e) => {
    e.stopPropagation();
    // An auto-height window adopts its rendered height the moment resizing starts.
    const cur = state.h > 0 ? state.h : root.getBoundingClientRect().height;
    rez = { px: e.clientX, py: e.clientY, w: state.w, h: cur };
    grip.setPointerCapture(e.pointerId);
  });
  grip.addEventListener('pointermove', (e) => {
    if (!rez) return;
    state.w = Math.max(minW, rez.w + (e.clientX - rez.px));
    state.h = Math.max(minH, rez.h + (e.clientY - rez.py));
    apply();
  });
  grip.addEventListener('pointerup', () => {
    if (!rez) return;
    rez = null;
    persist();
  });

  btnShade.onclick = () => { state.sh = !state.sh; apply(); persist(); };
  btnOpacity.onclick = () => { opIndex = (opIndex + 1) % OPACITY_STEPS.length; apply(); persist(); };
  btnClose.onclick = () => { setOpen(false); opts.onClose?.(); };

  window.addEventListener('resize', () => { apply(); });

  const setOpen = (open: boolean): void => {
    const was = root.style.display !== 'none';
    if (was !== open) {
      // Ambience hook — decoupled: AtlasAmbienceModule listens for these.
      window.dispatchEvent(new CustomEvent('atlas-ui-sound', { detail: open ? 'open' : 'close' }));
    }
    root.style.display = open ? 'flex' : 'none';
    if (open) {
      // Opening a window must SHOW it: a persisted shade (title-bar roll-up)
      // otherwise restores as a bare header — read as a broken empty window.
      // Shading stays available while the window remains open.
      if (state.sh) { state.sh = false; persist(); }
      apply();
      raise();
    }
  };

  document.body.appendChild(root);
  apply();

  return {
    root,
    body,
    setOpen,
    isOpen: () => root.style.display !== 'none',
    setTitle: (t: string) => { titleEl.textContent = t; },
  };
}

// ─── Adoption: windowize an EXISTING panel without touching its module ─────────

export interface AdoptOptions extends WindowOptions {
  /** How to read the panel's own open state (default: style.display !== 'none'). */
  isOpen?: (el: HTMLElement) => boolean;
  /** How the × should close the panel (default: style.display = 'none'). */
  close?: (el: HTMLElement) => void;
}

/**
 * Wrap an existing fixed/docked panel in window chrome. The OWNING MODULE keeps
 * toggling its panel exactly as before (a `.show` class or style.display) — a
 * MutationObserver mirrors that state onto the window, and the window's ×
 * closes through the panel's own mechanism, so hotkeys, menu-bar buttons, and
 * the chrome can never disagree. The panel's dock styling (position:fixed,
 * side borders, width caps) is neutralized inline; the stylesheet's `.show`
 * display toggle keeps working inside the window body.
 */
export function adoptWindow(content: HTMLElement, opts: AdoptOptions): UiWindow {
  const isOpenFn = opts.isOpen ?? ((el: HTMLElement) => el.style.display !== 'none');
  const closeFn = opts.close ?? ((el: HTMLElement) => { el.style.display = 'none'; });
  const win = makeWindow({ ...opts, onClose: () => { closeFn(content); opts.onClose?.(); } });

  content.style.position = 'static';
  content.style.inset = 'auto';
  content.style.width = '100%';
  content.style.height = '100%';
  content.style.maxWidth = 'none';
  content.style.border = 'none';
  content.style.borderRadius = '0';
  content.style.backdropFilter = 'none';
  content.style.zIndex = 'auto';
  content.style.boxSizing = 'border-box';
  win.body.appendChild(content);

  const sync = (): void => {
    const open = isOpenFn(content);
    if (open !== win.isOpen()) win.setOpen(open);
  };
  new MutationObserver(sync).observe(content, { attributes: true, attributeFilter: ['style', 'class'] });
  sync();
  return win;
}
